import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CreditCard, DollarSign, Eye, RefreshCw, Search, TrendingDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  BuyRenewalFailure,
  BuyRenewalFailuresSummary,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./RenewalFailures.module.css";

const PAGE_SIZE = 20;

const emptySummary: BuyRenewalFailuresSummary = {
  failed_renewals: 0,
  mrr_at_risk: 0,
  already_churned: 0,
  still_recoverable: 0,
};

const formatCurrency = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value: string | null | undefined) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US");
};

const statusClass = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized === "churned") return styles.statusChurned;
  if (normalized === "paused") return styles.statusPaused;
  return styles.statusActive;
};

const titleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const BuyAdminRenewalFailures = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<BuyRenewalFailure[]>([]);
  const [summary, setSummary] = useState<BuyRenewalFailuresSummary>(emptySummary);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadFailures = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await buyAdminApi.getRenewalFailures({
        search: search.trim() || undefined,
        from: from || undefined,
        to: to || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setRows(response.data || []);
      setSummary(response.summary || emptySummary);
      setTotal(response.pagination?.total || 0);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load renewal failures.");
      setRows([]);
      setSummary(emptySummary);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFailures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, from, to, page]);

  const visibleCount = useMemo(() => Math.min(PAGE_SIZE, rows.length), [rows.length]);

  return (
    <div className={`${styles.page} buy-admin-theme-scope`} data-testid="renewal-failures-page">
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Membership Renewal Failures</h1>
          <p>Track Stripe payment failures during membership renewal</p>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          <div className={styles.dateFilter}>
            <span>Filter by Failure Date</span>
            <input type="date" value={from} onChange={(event) => { setFrom(event.target.value); setPage(1); }} />
            <input type="date" value={to} onChange={(event) => { setTo(event.target.value); setPage(1); }} />
          </div>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void loadFailures()}
            disabled={loading}
            aria-label="Refresh renewal failures"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? styles.spin : undefined} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      <div className={styles.summaryGrid}>
        <section className={`${styles.summaryCard} ${styles.redBorder} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--red`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <AlertTriangle size={18} />
            </div>
            <span className="buy-admin-stat-label">Failed Renewals</span>
          </div>
          <strong className={styles.redText}>{summary.failed_renewals.toLocaleString()}</strong>
        </section>
        <section className={`${styles.summaryCard} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--amber`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <DollarSign size={18} />
            </div>
            <span className="buy-admin-stat-label">MRR at Risk</span>
          </div>
          <strong className={styles.amberText}>{formatCurrency(summary.mrr_at_risk)}</strong>
        </section>
        <section className={`${styles.summaryCard} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--red`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <TrendingDown size={18} />
            </div>
            <span className="buy-admin-stat-label">Already Churned</span>
          </div>
          <strong className={styles.redText}>{summary.already_churned.toLocaleString()}</strong>
        </section>
        <section className={`${styles.summaryCard} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--green`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <RefreshCw size={18} />
            </div>
            <span className="buy-admin-stat-label">Still Recoverable</span>
          </div>
          <strong className={styles.greenText}>{summary.still_recoverable.toLocaleString()}</strong>
        </section>
      </div>

      {summary.still_recoverable > 0 && (
      <section className={styles.alertCard}>
        <AlertTriangle size={24} />
        <div>
          <strong>Action Required</strong>
          <p>
            {summary.still_recoverable.toLocaleString()} members have failed payments but haven't churned yet.
            Contact them to update payment information and retain their membership.
          </p>
        </div>
      </section>
      )}

      <section className={styles.searchCard}>
        <div className={styles.searchBox}>
          <Search size={17} />
          <input
            value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(1); }}
            placeholder="Search by member name or email..."
          />
        </div>
      </section>

      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

      <section className={styles.tableCard}>
        {loading ? (
          <div className={styles.emptyState}>Loading renewal failures...</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Failed Date</th>
                  <th>Last Successful</th>
                  <th>Amount Due</th>
                  <th>Payment Method</th>
                  <th>Failure Reason</th>
                  <th>Status</th>
                  <th>Retries</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((failure) => (
                  <tr key={failure.id} className={failure.current_status === "churned" ? styles.churnedRow : ""}>
                    <td>
                      <button
                        className={styles.memberButton}
                        type="button"
                        onClick={() => failure.memberId && navigate(`/buy-user-admin-management/${failure.memberId}`)}
                      >
                        {failure.member}
                      </button>
                      <span>{failure.email || "-"}</span>
                    </td>
                    <td>
                      {formatDate(failure.failed_renewal_date)}
                      <span className={styles.overdueText}>{failure.days_overdue} days overdue</span>
                    </td>
                    <td>{formatDate(failure.last_successful_payment)}</td>
                    <td className={styles.amountCell}>{formatCurrency(failure.amount_due)}</td>
                    <td>
                      <div className={styles.paymentCell}>
                        <CreditCard size={16} />
                        <span>{failure.card_brand} {failure.card_last4 ? `**** ${failure.card_last4}` : ""}</span>
                      </div>
                    </td>
                    <td><span className={styles.reasonBadge}>{failure.failure_reason}</span></td>
                    <td className={styles.center}>
                      <span className={`${styles.statusBadge} ${statusClass(failure.current_status)}`}>
                        {titleCase(failure.current_status)}
                      </span>
                    </td>
                    <td className={styles.center}>
                      <span className={failure.retry_attempts >= 3 ? styles.retryBadgeHot : styles.retryBadge}>
                        {failure.retry_attempts}
                      </span>
                    </td>
                    <td className={styles.center}>
                      <div className={styles.actions}>
                        <button
                          className={styles.iconButton}
                          type="button"
                          onClick={() => failure.memberId && navigate(`/buy-user-admin-management/${failure.memberId}`)}
                          aria-label="View member"
                        >
                          <Eye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className={styles.emptyCell}>No renewal failures found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className={styles.footer}>
          <span>Showing {visibleCount} of {total.toLocaleString()} failed renewals</span>
          <div className={styles.pagination}>
            <button type="button" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>Next</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BuyAdminRenewalFailures;
